import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../store/reducers/snackbarSlice';
import {
  AddressParts,
  Company,
  CompanyLocation,
  SettlementSuggestion,
  StreetSuggestion,
} from '../../../types';
import FormHeader from '../СompanyForm/FormHeader/FormHeader';
import FormSection from '../СompanyForm/FormSection/FormSection';
import {
  addCompanyLocation,
  getCompanyLocations,
  parseAddress,
  regeocodeCompanyLocation,
  setCompanyLocationLegal,
  setCompanyLocationPoint,
  removeCompanyLocation,
  searchSettlements,
  searchSettlementStreets,
} from '../companyHelpers';
import style from './CompanyLocationsModal.module.css';

/*
 * §FB31/§FB32 «Локації»: адреси, де впроваджено переклад жестової мови WordCraft.
 * ОСНОВНИЙ потік — «Швидкий ввід»: адмін набирає 2-3 літери і ОБИРАЄ населений пункт
 * та вулицю з живого реєстру Нової Пошти (як на сайті НП) → вводить лише № будинку.
 * Реєстрові Ref-и зберігаються — одруки в назвах неможливі. Другорядний потік —
 * вставка повної адреси одним рядком з авто-розбором. При збереженні бекенд сам
 * визначає гео-координати (для майбутньої карти впроваджень).
 */

interface CompanyLocationsModalProps {
  companyId: number;
  company?: Company;
  onClose: () => void;
  /** §FB37: рендер як вкладка сторінки компанії — без оверлею і власного хедера. */
  embedded?: boolean;
}

const PinGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" />
  </svg>
);
const ListGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M4 6h2v2H4V6zm4 0h12v2H8V6zm-4 5h2v2H4v-2zm4 0h12v2H8v-2zm-4 5h2v2H4v-2zm4 0h12v2H8v-2z" />
  </svg>
);
const TrashGlyph = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

/* Короткі форми типів — як їх пишуть у реєстрах. */
const SETTLEMENT_SHORT: Record<string, string> = {
  місто: 'м.',
  'селище міського типу': 'смт',
  село: 'с.',
  селище: 'с-ще',
};
const STREET_SHORT: Record<string, string> = {
  вулиця: 'вул.',
  проспект: 'просп.',
  провулок: 'пров.',
  бульвар: 'бульв.',
  площа: 'пл.',
  набережна: 'наб.',
  мікрорайон: 'мкрн',
  'житловий масив': 'ж/м',
};
const UNIT_SHORT: Record<string, string> = {
  квартира: 'кв.',
  офіс: 'офіс',
  кабінет: 'каб.',
  приміщення: 'прим.',
  кімната: 'кімн.',
};

const mainLine = (p: AddressParts): string => {
  const items: string[] = [];
  if (p.settlement) {
    const t = p.settlementType
      ? SETTLEMENT_SHORT[p.settlementType] || p.settlementType
      : '';
    items.push([t, p.settlement].filter(Boolean).join(' '));
  }
  if (p.street) {
    const t = p.streetType ? STREET_SHORT[p.streetType] || p.streetType : '';
    items.push([t, p.street].filter(Boolean).join(' '));
  }
  if (p.building) items.push(`буд. ${p.building}`);
  if (p.unit) {
    const t = p.unitType ? UNIT_SHORT[p.unitType] || p.unitType : 'прим.';
    items.push(`${t} ${p.unit}`);
  }
  return items.join(', ') || '—';
};

const subLine = (p: AddressParts): string => {
  const items: string[] = [];
  if (p.region) items.push(`${p.region} обл.`);
  if (p.district) items.push(`${p.district} р-н`);
  if (p.hromada) items.push(p.hromada);
  if (p.postalCode) items.push(p.postalCode);
  // Країну не показуємо: працюємо лише по Україні, вона однакова в усіх рядках.
  return items.join(' · ');
};

/* Редаговане поле складової (режим «Рядком»): назва над інпутом. */
const PartField: React.FC<{
  label: string;
  value?: string | null;
  onChange: (v: string) => void;
}> = ({ label, value, onChange }) => (
  <div className={style.pField}>
    <span className={style.pLabel}>{label}</span>
    <input
      className={style.pInput}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const CompanyLocationsModal: React.FC<CompanyLocationsModalProps> = ({
  companyId,
  company,
  onClose,
  embedded = false,
}) => {
  const dispatch = useDispatch();
  const [locations, setLocations] = useState<CompanyLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'quick' | 'paste'>('quick');
  const [saving, setSaving] = useState(false);

  /* ---- Режим «Рядком» ---- */
  const [addressInput, setAddressInput] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState<AddressParts | null>(null);

  /* ---- Режим «Швидкий ввід» (typeahead з реєстру НП) ---- */
  const [settlementQuery, setSettlementQuery] = useState('');
  const [settlementOptions, setSettlementOptions] = useState<
    SettlementSuggestion[]
  >([]);
  const [pickedSettlement, setPickedSettlement] =
    useState<SettlementSuggestion | null>(null);
  const [streetQuery, setStreetQuery] = useState('');
  const [streetOptions, setStreetOptions] = useState<StreetSuggestion[]>([]);
  const [pickedStreet, setPickedStreet] = useState<StreetSuggestion | null>(
    null,
  );
  const [streetNoResults, setStreetNoResults] = useState(false);
  const [building, setBuilding] = useState('');
  const [unitType, setUnitType] = useState('');
  const [unit, setUnit] = useState('');
  const streetInputRef = useRef<HTMLInputElement>(null);
  const buildingInputRef = useRef<HTMLInputElement>(null);
  // §FB33: локація, відкрита на міні-карті (клік по рядку списку).
  const [mapLocation, setMapLocation] = useState<CompanyLocation | null>(null);
  // §FB34: режим міні-карти — «Супутник» за замовчуванням (на OSM-схемі в малих містах
  // будинки часто ще не намальовані, на супутнику видно реальну забудову завжди).
  const [mapMode, setMapMode] = useState<'sat' | 'osm'>('sat');
  // §FB35: чернетка вручну уточненої точки (клік по супутнику) до натискання «Зберегти».
  const [pinDraft, setPinDraft] = useState<{ lat: number; lon: number } | null>(
    null,
  );
  const [savingPin, setSavingPin] = useState(false);

  // §FB34 супутник: розкладка статичних тайлів Esri World Imagery навколо точки
  // (slippy-map математика, z=17 — рівень окремих будинків; без ключів і бібліотек).
  const SAT_Z = 17;
  const satTiles = (lat: number, lon: number) => {
    const n = 2 ** SAT_Z;
    const xt = ((lon + 180) / 360) * n;
    const latR = (lat * Math.PI) / 180;
    const yt =
      ((1 - Math.log(Math.tan(latR) + 1 / Math.cos(latR)) / Math.PI) / 2) * n;
    const cx = Math.floor(xt);
    const cy = Math.floor(yt);
    const tiles: {
      key: string;
      x: number;
      y: number;
      dx: number;
      dy: number;
    }[] = [];
    for (let x = cx - 2; x <= cx + 2; x += 1) {
      for (let y = cy - 1; y <= cy + 1; y += 1) {
        tiles.push({
          key: `${x}:${y}`,
          x,
          y,
          dx: (x - xt) * 256,
          dy: (y - yt) * 256,
        });
      }
    }
    return tiles;
  };
  const [regeocodingId, setRegeocodingId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLocations(await getCompanyLocations(companyId));
      } catch {
        dispatch(
          showSnackbar({
            message: 'Не вдалося завантажити локації',
            type: 'error',
          }),
        );
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  /* Дебаунс-пошук населеного пункту в реєстрі НП (від 2 літер). */
  useEffect(() => {
    const q = settlementQuery.trim();
    if (pickedSettlement || q.length < 2) {
      setSettlementOptions([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        setSettlementOptions(await searchSettlements(q));
      } catch {
        setSettlementOptions([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [settlementQuery, pickedSettlement]);

  /* Дебаунс-пошук вулиці в межах обраного пункту. */
  useEffect(() => {
    const q = streetQuery.trim();
    if (!pickedSettlement || pickedStreet || q.length < 2) {
      setStreetOptions([]);
      setStreetNoResults(false);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const found = await searchSettlementStreets(pickedSettlement.ref, q);
        setStreetOptions(found);
        // Для малих сіл/смт довідник вулиць НП порожній — кажемо про це чесно,
        // ввід вручну при цьому працює (вулиця збережеться як набрана).
        setStreetNoResults(found.length === 0);
      } catch {
        setStreetOptions([]);
        setStreetNoResults(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [streetQuery, pickedSettlement, pickedStreet]);

  const pickSettlement = (s: SettlementSuggestion) => {
    setPickedSettlement(s);
    setSettlementQuery(s.present || s.name);
    setSettlementOptions([]);
    // Пункт обрано → одразу до вулиці.
    setTimeout(() => streetInputRef.current?.focus(), 0);
  };

  const resetSettlement = (value: string) => {
    setSettlementQuery(value);
    setPickedSettlement(null);
    setPickedStreet(null);
    setStreetQuery('');
    setStreetOptions([]);
  };

  const pickStreet = (s: StreetSuggestion) => {
    setPickedStreet(s);
    setStreetQuery(s.present || s.name);
    setStreetOptions([]);
    setTimeout(() => buildingInputRef.current?.focus(), 0);
  };

  const quickValid = !!pickedSettlement && building.trim().length > 0;

  const buildQuickParts = (): AddressParts => ({
    country: 'Україна',
    region: pickedSettlement?.region ?? null,
    district: pickedSettlement?.district ?? null,
    settlementType: pickedSettlement?.settlementType ?? null,
    settlement: pickedSettlement?.name ?? null,
    streetType: pickedStreet?.streetType ?? null,
    street: pickedStreet?.name || streetQuery.trim() || null,
    buildingType: 'будинок',
    building: building.trim(),
    unitType: unit.trim() ? unitType || 'приміщення' : null,
    unit: unit.trim() || null,
  });

  const buildQuickRaw = (p: AddressParts): string => {
    const items: string[] = ['Україна'];
    if (p.region) items.push(`${p.region} обл.`);
    if (p.district) items.push(`${p.district} р-н`);
    const st = p.settlementType
      ? SETTLEMENT_SHORT[p.settlementType] || p.settlementType
      : '';
    items.push([st, p.settlement].filter(Boolean).join(' '));
    if (p.street) {
      const t = p.streetType ? STREET_SHORT[p.streetType] || p.streetType : '';
      items.push([t, p.street].filter(Boolean).join(' '));
    }
    if (p.building) items.push(`буд. ${p.building}`);
    if (p.unit) {
      items.push(
        `${UNIT_SHORT[p.unitType || ''] || p.unitType || 'прим.'} ${p.unit}`,
      );
    }
    return items.join(', ');
  };

  // Бекенд віддає текст помилки з великої «Message» — читаємо обидва варіанти,
  // щоб «Така локація вже додана…» не маскувалась загальним «Не вдалося зберегти».
  const errorMessage = (e: unknown, fallback: string): string => {
    const maybe = e as {
      response?: { data?: { message?: string; Message?: string } };
    };
    return (
      maybe?.response?.data?.Message ||
      maybe?.response?.data?.message ||
      fallback
    );
  };

  const afterCreated = (created: CompanyLocation) => {
    setLocations((prev) => [created, ...prev]);
    dispatch(
      showSnackbar({
        message:
          created.latitude != null
            ? 'Локацію додано (координати визначено)'
            : 'Локацію додано (без координат — добрати можна пізніше)',
        type: 'success',
      }),
    );
  };

  const handleQuickSave = async () => {
    if (!quickValid || saving) return;
    setSaving(true);
    try {
      const parts = buildQuickParts();
      const created = await addCompanyLocation(
        companyId,
        buildQuickRaw(parts),
        parts,
        pickedSettlement?.ref,
        pickedStreet?.ref,
      );
      afterCreated(created);
      resetSettlement('');
      setBuilding('');
      setUnitType('');
      setUnit('');
    } catch (e) {
      dispatch(
        showSnackbar({
          message: errorMessage(e, 'Не вдалося зберегти локацію'),
          type: 'error',
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleParse = async () => {
    const address = addressInput.trim();
    if (!address || parsing) return;
    setParsing(true);
    try {
      setParsed(await parseAddress(address));
    } catch {
      dispatch(
        showSnackbar({ message: 'Не вдалося розібрати адресу', type: 'error' }),
      );
    } finally {
      setParsing(false);
    }
  };

  // Аудит 1c: не даємо зберегти розбір без населеного пункту.
  const pasteValid = !!parsed && !!(parsed.settlement || '').trim();

  const handlePasteSave = async () => {
    if (!parsed || !pasteValid || saving) return;
    setSaving(true);
    try {
      const created = await addCompanyLocation(
        companyId,
        addressInput.trim(),
        parsed,
      );
      afterCreated(created);
      setAddressInput('');
      setParsed(null);
    } catch (e) {
      dispatch(
        showSnackbar({
          message: errorMessage(e, 'Не вдалося зберегти локацію'),
          type: 'error',
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (locationId: number) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Видалити цю локацію?')) return;
    try {
      await removeCompanyLocation(locationId);
      setLocations((prev) => prev.filter((l) => l.id !== locationId));
      dispatch(showSnackbar({ message: 'Локацію видалено', type: 'success' }));
    } catch {
      dispatch(
        showSnackbar({
          message: 'Не вдалося видалити локацію',
          type: 'error',
        }),
      );
    }
  };

  const setPart = (key: keyof AddressParts) => (v: string) =>
    setParsed((prev) => (prev ? { ...prev, [key]: v } : prev));

  // §FB33: клік по рядку — відкриваємо локацію на міні-карті (якщо є координати).
  const handleRowClick = (loc: CompanyLocation) => {
    if (loc.latitude == null || loc.longitude == null) {
      dispatch(
        showSnackbar({
          message:
            'У цієї локації ще немає координат — натисніть «Оновити координати» (🔄)',
          type: 'error',
        }),
      );
      return;
    }
    setPinDraft(null);
    setMapLocation(loc);
  };

  // §FB35: клік по супутнику → чернетка точки (зворотна slippy-математика від центру карти).
  const handleSatClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      !mapLocation ||
      mapLocation.latitude == null ||
      mapLocation.longitude == null
    ) {
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const dxPx = e.clientX - (rect.left + rect.width / 2);
    const dyPx = e.clientY - (rect.top + rect.height / 2);
    const n = 2 ** SAT_Z;
    const xt0 = ((mapLocation.longitude + 180) / 360) * n;
    const latR = (mapLocation.latitude * Math.PI) / 180;
    const yt0 =
      ((1 - Math.log(Math.tan(latR) + 1 / Math.cos(latR)) / Math.PI) / 2) * n;
    const xt = xt0 + dxPx / 256;
    const yt = yt0 + dyPx / 256;
    const lon = (xt / n) * 360 - 180;
    const lat =
      (Math.atan(Math.sinh(Math.PI * (1 - (2 * yt) / n))) * 180) / Math.PI;
    setPinDraft({ lat, lon });
  };

  // §FB36 тумблер «юридична адреса»: бекенд гарантує одну на компанію і повертає
  // оновлений рядок; список перечитуємо, щоб юридична стала першою.
  const [legalTogglingId, setLegalTogglingId] = useState<number | null>(null);
  const handleToggleLegal = async (
    e: React.MouseEvent,
    loc: CompanyLocation,
  ) => {
    e.stopPropagation();
    if (legalTogglingId !== null) return;
    setLegalTogglingId(loc.id);
    try {
      await setCompanyLocationLegal(loc.id, !loc.isLegal);
      setLocations(await getCompanyLocations(companyId));
      dispatch(
        showSnackbar({
          message: loc.isLegal
            ? 'Позначку «юридична адреса» знято'
            : 'Локацію позначено юридичною адресою — вона тепер перша в списку',
          type: 'success',
        }),
      );
    } catch (err) {
      dispatch(
        showSnackbar({
          message: errorMessage(err, 'Не вдалося змінити позначку'),
          type: 'error',
        }),
      );
    } finally {
      setLegalTogglingId(null);
    }
  };

  // §FB35: зберегти вручну уточнену точку (точність «будинок», джерело «manual»).
  const handleSavePin = async () => {
    if (!mapLocation || !pinDraft || savingPin) return;
    setSavingPin(true);
    try {
      const updated = await setCompanyLocationPoint(
        mapLocation.id,
        pinDraft.lat,
        pinDraft.lon,
      );
      setLocations((prev) =>
        prev.map((l) => (l.id === updated.id ? updated : l)),
      );
      setMapLocation(updated);
      setPinDraft(null);
      dispatch(
        showSnackbar({
          message: 'Точку збережено — тепер це точне розташування будинку',
          type: 'success',
        }),
      );
    } catch (err) {
      dispatch(
        showSnackbar({
          message: errorMessage(err, 'Не вдалося зберегти точку'),
          type: 'error',
        }),
      );
    } finally {
      setSavingPin(false);
    }
  };

  // §FB35: піксельний зсув чернетки відносно збереженої точки (для мітки на супутнику).
  const pinDraftOffset = (() => {
    if (
      !pinDraft ||
      !mapLocation ||
      mapLocation.latitude == null ||
      mapLocation.longitude == null
    ) {
      return null;
    }
    const n = 2 ** SAT_Z;
    const fx = (lon: number) => ((lon + 180) / 360) * n;
    const fy = (lat: number) => {
      const r = (lat * Math.PI) / 180;
      return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * n;
    };
    return {
      dx: (fx(pinDraft.lon) - fx(mapLocation.longitude)) * 256,
      dy: (fy(pinDraft.lat) - fy(mapLocation.latitude)) * 256,
    };
  })();

  // §FB33 «оновити координати»: повторний прогін геокодера по збереженому рядку.
  const handleRegeocode = async (e: React.MouseEvent, locationId: number) => {
    e.stopPropagation();
    if (regeocodingId !== null) return;
    // §FB35: вручну уточнену точку не перезаписуємо мовчки.
    // §FB80: тепер це правило стоїть і НА СЕРВЕРІ — таку точку міг поставити працівник
    // з телефона, стоячи біля входу. Тому підтвердження не просто пропускає нас далі,
    // а їде на сервер прапорцем `force`: без нього регеокодування отримає відмову.
    const target = locations.find((l) => l.id === locationId);
    const isManual = target?.geoSource === 'manual';
    if (
      isManual &&
      !window.confirm(
        'Цю точку встановлено вручну (можливо — працівником на місці). ' +
          'Перезаписати її автоматичним геокодуванням?',
      )
    ) {
      return;
    }
    setRegeocodingId(locationId);
    try {
      const updated = await regeocodeCompanyLocation(locationId, isManual);
      setLocations((prev) =>
        prev.map((l) => (l.id === locationId ? updated : l)),
      );
      dispatch(
        showSnackbar({
          message: `Координати оновлено (точність: ${
            updated.geoPrecision || 'н/д'
          })`,
          type: 'success',
        }),
      );
    } catch (err) {
      dispatch(
        showSnackbar({
          message: errorMessage(err, 'Не вдалося оновити координати'),
          type: 'error',
        }),
      );
    } finally {
      setRegeocodingId(null);
    }
  };

  const handleWrapperClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className={embedded ? style.embeddedWrap : style.formWrapper}
      onClick={embedded ? undefined : handleWrapperClick}
    >
      <div className={embedded ? style.embeddedCard : style.modalWrap}>
        {!embedded && (
          <FormHeader
            title={`Локації: ${company?.name || ''}`}
            onClose={onClose}
          />
        )}
        <div className={style.body}>
          <FormSection
            title="Додати локацію"
            subtitle="Оберіть пункт і вулицю з реєстру — вводити вручну треба лише № будинку"
            icon={<PinGlyph />}
            headerRight={
              <div className={style.modeSwitch}>
                <button
                  type="button"
                  className={mode === 'quick' ? style.modeOn : style.modeOff}
                  onClick={() => setMode('quick')}
                >
                  Швидкий ввід
                </button>
                <button
                  type="button"
                  className={mode === 'paste' ? style.modeOn : style.modeOff}
                  onClick={() => setMode('paste')}
                >
                  Рядком
                </button>
              </div>
            }
          >
            {mode === 'quick' ? (
              <>
                <div className={style.quickGrid}>
                  <div className={`${style.pField} ${style.qSettlement}`}>
                    <span className={style.pLabel}>Населений пункт</span>
                    <div className={style.suggestWrap}>
                      <input
                        className={style.pInput}
                        placeholder="Почніть вводити: Цебр…"
                        value={settlementQuery}
                        onChange={(e) => resetSettlement(e.target.value)}
                        autoFocus
                      />
                      {settlementOptions.length > 0 && (
                        <div className={style.suggestList}>
                          {settlementOptions.map((s) => (
                            <button
                              key={s.ref}
                              type="button"
                              className={style.suggestItem}
                              onClick={() => pickSettlement(s)}
                            >
                              {s.present || s.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={`${style.pField} ${style.qStreet}`}>
                    <span className={style.pLabel}>Вулиця</span>
                    <div className={style.suggestWrap}>
                      <input
                        ref={streetInputRef}
                        className={style.pInput}
                        placeholder={
                          pickedSettlement
                            ? 'Почніть вводити назву вулиці…'
                            : 'Спочатку оберіть пункт'
                        }
                        value={streetQuery}
                        onChange={(e) => {
                          setStreetQuery(e.target.value);
                          setPickedStreet(null);
                        }}
                        disabled={!pickedSettlement}
                      />
                      {streetOptions.length > 0 && (
                        <div className={style.suggestList}>
                          {streetOptions.map((s) => (
                            <button
                              key={s.ref}
                              type="button"
                              className={style.suggestItem}
                              onClick={() => pickStreet(s)}
                            >
                              {s.present || s.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={`${style.pField} ${style.qSmall}`}>
                    <span className={style.pLabel}>Будинок *</span>
                    <input
                      ref={buildingInputRef}
                      className={style.pInput}
                      placeholder="15А"
                      value={building}
                      onChange={(e) => setBuilding(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleQuickSave();
                        }
                      }}
                    />
                  </div>
                  <div className={`${style.pField} ${style.qSmall}`}>
                    <span className={style.pLabel}>Приміщення</span>
                    <div className={style.unitRow}>
                      <select
                        className={style.unitSelect}
                        value={unitType}
                        onChange={(e) => setUnitType(e.target.value)}
                      >
                        <option value="">тип…</option>
                        <option value="квартира">кв.</option>
                        <option value="офіс">офіс</option>
                        <option value="кабінет">каб.</option>
                        <option value="приміщення">прим.</option>
                      </select>
                      <input
                        className={style.pInput}
                        placeholder="№"
                        value={unit}
                        onChange={(e) => setUnit(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                {pickedSettlement && (
                  <p className={style.registryHint}>
                    ✓ З реєстру: {pickedSettlement.present}
                    {pickedStreet ? ` · ${pickedStreet.present}` : ''}
                  </p>
                )}
                {streetNoResults && (
                  <p className={style.noStreetsHint}>
                    У довіднику НП немає такої вулиці для цього пункту (для
                    малих сіл/смт вуличного реєстру може не бути) — введіть
                    назву вручну, локація збережеться як набрано.
                  </p>
                )}
                <div className={style.saveRow}>
                  <button
                    type="button"
                    className={style.saveBtn}
                    onClick={handleQuickSave}
                    disabled={!quickValid || saving}
                  >
                    {saving ? 'Зберігаємо…' : 'Зберегти локацію'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className={style.addRow}>
                  <input
                    className={style.addrInput}
                    placeholder="Україна, 67130, Одеська обл., Роздільнянський р-н, селище міського типу Цебрикове, вул. Авдієвського Анатолія, будинок 15А"
                    value={addressInput}
                    onChange={(e) => {
                      setAddressInput(e.target.value);
                      // Аудит 1a: правка рядка після розбору скидає складові —
                      // інакше збережеться розбір СТАРОЇ адреси з новим рядком.
                      if (parsed) setParsed(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleParse();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className={style.parseBtn}
                    onClick={handleParse}
                    disabled={!addressInput.trim() || parsing}
                  >
                    {parsing ? 'Розбираємо…' : 'Розібрати адресу'}
                  </button>
                </div>

                {parsed && (
                  <>
                    <div className={style.partsGrid}>
                      <PartField
                        label="Країна"
                        value={parsed.country}
                        onChange={setPart('country')}
                      />
                      <PartField
                        label="Індекс"
                        value={parsed.postalCode}
                        onChange={setPart('postalCode')}
                      />
                      <PartField
                        label="Область"
                        value={parsed.region}
                        onChange={setPart('region')}
                      />
                      <PartField
                        label="Район"
                        value={parsed.district}
                        onChange={setPart('district')}
                      />
                      <PartField
                        label="Тип нас. пункту"
                        value={parsed.settlementType}
                        onChange={setPart('settlementType')}
                      />
                      <PartField
                        label="Населений пункт"
                        value={parsed.settlement}
                        onChange={setPart('settlement')}
                      />
                      <PartField
                        label="Тип вулиці"
                        value={parsed.streetType}
                        onChange={setPart('streetType')}
                      />
                      <PartField
                        label="Вулиця"
                        value={parsed.street}
                        onChange={setPart('street')}
                      />
                      <PartField
                        label="Будинок"
                        value={parsed.building}
                        onChange={setPart('building')}
                      />
                      <PartField
                        label="Тип приміщення"
                        value={parsed.unitType}
                        onChange={setPart('unitType')}
                      />
                      <PartField
                        label="№ приміщення"
                        value={parsed.unit}
                        onChange={setPart('unit')}
                      />
                      <PartField
                        label="Громада"
                        value={parsed.hromada}
                        onChange={setPart('hromada')}
                      />
                    </div>
                    {parsed.note && (
                      <p className={style.note}>
                        Примітка парсера: {parsed.note}
                      </p>
                    )}
                    <div className={style.saveRow}>
                      <button
                        type="button"
                        className={style.saveBtn}
                        onClick={handlePasteSave}
                        disabled={!pasteValid || saving}
                        title={
                          pasteValid
                            ? undefined
                            : 'Заповніть населений пункт перед збереженням'
                        }
                      >
                        {saving ? 'Зберігаємо…' : 'Зберегти локацію'}
                      </button>
                      <button
                        type="button"
                        className={style.cancelBtn}
                        onClick={() => setParsed(null)}
                        disabled={saving}
                      >
                        Скасувати
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </FormSection>

          <FormSection
            title="Локації компанії"
            subtitle="Адреси, де впроваджено переклад жестової мови WordCraft"
            icon={<ListGlyph />}
          >
            {loading ? (
              <p className={style.empty}>Завантаження…</p>
            ) : locations.length === 0 ? (
              <p className={style.empty}>
                Локацій ще немає — додайте першу адресу вище.
              </p>
            ) : (
              <div className={style.locList}>
                {locations.map((loc, i) => (
                  <div
                    key={loc.id}
                    className={`${style.locRow} ${style.locRowClickable}`}
                    title={`${loc.rawAddress} — натисніть, щоб відкрити на карті`}
                    onClick={() => handleRowClick(loc)}
                  >
                    <span className={style.locNum}>{i + 1}</span>
                    <div className={style.locText}>
                      <p className={style.locMain}>{mainLine(loc.parts)}</p>
                      {subLine(loc.parts) && (
                        <p className={style.locSub}>{subLine(loc.parts)}</p>
                      )}
                    </div>
                    {loc.isLegal && (
                      <span
                        className={style.legalChip}
                        title="Юридична адреса компанії — завжди перша в списку"
                      >
                        ⚖ юридична
                      </span>
                    )}
                    {loc.latitude != null && loc.longitude != null ? (
                      <span
                        className={style.geoChip}
                        title={`${loc.latitude.toFixed(6)}, ${loc.longitude.toFixed(6)} — точність: ${
                          loc.geoPrecision || 'н/д'
                        } (будинок — найточніше; вулиця/нас. пункт — наскільки OSM знає цю адресу)`}
                      >
                        📍 {loc.geoPrecision || 'координати'}
                      </span>
                    ) : (
                      <span
                        className={style.geoChipMuted}
                        title="Координати не визначені — натисніть 🔄"
                      >
                        без координат
                      </span>
                    )}
                    <button
                      type="button"
                      className={
                        loc.isLegal ? style.locLegalActive : style.locLegal
                      }
                      onClick={(e) => handleToggleLegal(e, loc)}
                      disabled={legalTogglingId !== null}
                      title={
                        loc.isLegal
                          ? 'Зняти позначку «юридична адреса»'
                          : 'Позначити юридичною адресою (стане першою в списку)'
                      }
                    >
                      {legalTogglingId === loc.id ? '…' : '⚖'}
                    </button>
                    <button
                      type="button"
                      className={style.locRefresh}
                      onClick={(e) => handleRegeocode(e, loc.id)}
                      disabled={regeocodingId !== null}
                      title="Оновити координати (повторне геокодування)"
                    >
                      {regeocodingId === loc.id ? '…' : '🔄'}
                    </button>
                    <button
                      type="button"
                      className={style.locDelete}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(loc.id);
                      }}
                      title="Видалити локацію"
                    >
                      <TrashGlyph />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </FormSection>
        </div>
      </div>

      {/* §FB33/§FB34: міні-карта локації — Супутник (Esri, видно реальну забудову) або
          Схема (OSM embed з маркером); зовнішні лінки OSM + Google Maps. */}
      {mapLocation &&
        mapLocation.latitude != null &&
        mapLocation.longitude != null && (
          <div
            className={style.mapOverlay}
            onClick={(e) => {
              if (e.target === e.currentTarget) setMapLocation(null);
            }}
          >
            <div className={style.mapCard}>
              <div className={style.mapHeader}>
                <div className={style.mapTitleWrap}>
                  <p className={style.mapTitle}>
                    {mainLine(mapLocation.parts)}
                  </p>
                  {subLine(mapLocation.parts) && (
                    <p className={style.mapSubtitle}>
                      {subLine(mapLocation.parts)}
                    </p>
                  )}
                  {mapLocation.geoPrecision &&
                    mapLocation.geoPrecision !== 'будинок' && (
                      <p className={style.mapPrecisionHint}>
                        Точність «{mapLocation.geoPrecision}»:{' '}
                        {mapLocation.geoPrecision === 'вулиця'
                          ? 'мітка стоїть на вулиці — саме цього № будинку в картах ще немає'
                          : 'мітка в центрі населеного пункту'}
                      </p>
                    )}
                </div>
                <div className={style.mapModeBtns}>
                  <button
                    type="button"
                    className={
                      mapMode === 'sat'
                        ? style.mapModeBtnActive
                        : style.mapModeBtn
                    }
                    onClick={() => setMapMode('sat')}
                  >
                    Супутник
                  </button>
                  <button
                    type="button"
                    className={
                      mapMode === 'osm'
                        ? style.mapModeBtnActive
                        : style.mapModeBtn
                    }
                    onClick={() => setMapMode('osm')}
                  >
                    Схема
                  </button>
                </div>
                <div className={style.mapLinks}>
                  <a
                    className={style.mapExternal}
                    href={`https://www.openstreetmap.org/?mlat=${mapLocation.latitude}&mlon=${mapLocation.longitude}#map=17/${mapLocation.latitude}/${mapLocation.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    OpenStreetMap ↗
                  </a>
                  <a
                    className={style.mapExternal}
                    href={`https://www.google.com/maps?q=${mapLocation.latitude},${mapLocation.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Google Maps ↗
                  </a>
                </div>
                <button
                  type="button"
                  className={style.mapClose}
                  onClick={() => setMapLocation(null)}
                  title="Закрити карту"
                >
                  ✕
                </button>
              </div>
              {mapMode === 'osm' ? (
                <iframe
                  className={style.mapFrame}
                  title="Карта локації"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${
                    mapLocation.longitude - 0.006
                  }%2C${mapLocation.latitude - 0.003}%2C${
                    mapLocation.longitude + 0.006
                  }%2C${mapLocation.latitude + 0.003}&layer=mapnik&marker=${
                    mapLocation.latitude
                  }%2C${mapLocation.longitude}`}
                />
              ) : (
                <div className={style.satWrap} onClick={handleSatClick}>
                  {satTiles(mapLocation.latitude, mapLocation.longitude).map(
                    (t) => (
                      <img
                        key={t.key}
                        className={style.satTile}
                        alt=""
                        style={{
                          transform: `translate(${t.dx}px, ${t.dy}px)`,
                        }}
                        src={`https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${SAT_Z}/${t.y}/${t.x}`}
                      />
                    ),
                  )}
                  <div
                    className={style.satPin}
                    style={
                      pinDraftOffset
                        ? {
                            transform: `translate(calc(-50% + ${pinDraftOffset.dx}px), calc(-88% + ${pinDraftOffset.dy}px))`,
                          }
                        : undefined
                    }
                  >
                    📍
                  </div>
                  {!pinDraft && (
                    <span className={style.satHint}>
                      Бачите точне місце? Клікніть по карті — мітку можна
                      уточнити вручну
                    </span>
                  )}
                  {pinDraft && (
                    <div
                      className={style.pinBar}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span>Зберегти нову точку як точне розташування?</span>
                      <button
                        type="button"
                        className={style.pinSave}
                        onClick={handleSavePin}
                        disabled={savingPin}
                      >
                        {savingPin ? '…' : 'Зберегти точку'}
                      </button>
                      <button
                        type="button"
                        className={style.pinReset}
                        onClick={() => setPinDraft(null)}
                      >
                        Скинути
                      </button>
                    </div>
                  )}
                  <span className={style.satAttrib}>
                    Супутник © Esri — World Imagery
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default CompanyLocationsModal;
